import { inject, Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  TrialInsightsEnrolledPatientResponseType,
  TrialInsightsResponseGeneric,
  TrialInsightsState,
} from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsStore } from '@models/trial-insights/trial-insights.store';

import { GqlService } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { MainQuery } from '@shared/store/main/main.query';

type LoadingState = 'completed' | RequestType;
type RequestType = 'initial' | 'remaining';
type Response = TrialInsightsResponseGeneric<ResponseType>;
type ResponseType = TrialInsightsEnrolledPatientResponseType;
type State = Partial<TrialInsightsState['patientEnrolled']>;

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsFinancePatientsEnrolledStoreService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private store = inject(TrialInsightsStore);

  getRemainingInvestigatorInfo$(): Observable<unknown> {
    return this.mainQuery.select('trialKey').pipe(
      this.updateLoadingState('initial'),
      switchMap(() => this.gqlService.getRemainingInvestigatorInfo$()),
      this.processResponse(),
      this.updateLoadingState('completed')
    );
  }

  loadingState(type: LoadingState): State {
    const loadingState: State = {};

    if (type === 'initial') {
      loadingState.isLoading = true;
      loadingState.data = null;
    } else {
      loadingState.isLoading = false;
    }

    return loadingState;
  }

  processResponse() {
    const processFn = (response: Response, subscriber: Subscriber<unknown>) => {
      const { data } = response;

      this.store.update((state) => {
        return {
          ...state,
          patientEnrolled: {
            ...state.patientEnrolled,
            data: data ? [data] : [],
          },
        };
      });

      subscriber.next(data);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  updateLoadingState(type: LoadingState) {
    const updates = this.loadingState(type);

    const processFn = (sourceValue: unknown, subscriber: Subscriber<unknown>) => {
      this.store.update((state) => {
        return {
          ...state,
          patientEnrolled: {
            ...state.patientEnrolled,
            ...updates,
            data: [...(state.patientEnrolled.data || []), ...(updates.data || [])],
          },
        };
      });

      subscriber.next(updates);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }
}
